import * as React from 'react';
import * as ReactDom from 'react-dom';
import injectSheet from 'react-jss';
import { ISnackbarContext, ISnackbarMessage, ISnackbarProviderClasses, ISnackbarProviderProps, SnackbarKey, SnackbarVariant } from '.';
import Snackbar from './Snackbar';
import { SnackbarContext } from './SnackbarContext';
import { SnackbarProviderClasses } from './Snackbar.jss';
const MAX_VISIBLE_SNACKBARS = 3;
const UnstyledSnackbarProvider = (props: ISnackbarProviderProps) => {
  const classes = props.classes as ISnackbarProviderClasses<string>;
  const [snackbars, setSnackbars] = React.useState<ISnackbarMessage[]>([]);
  const nextKey = React.useRef(1);
  const enqueueSnackbar = React.useCallback((message: React.ReactNode, variant: SnackbarVariant = 'info'): SnackbarKey => {
    const key = nextKey.current++;
    setSnackbars(currentSnackbars => currentSnackbars.concat({
      key,
      message,
      variant,
      exiting: false
    }));
    return key;
  }, []);
  const closeSnackbar = React.useCallback((key: SnackbarKey) => {
    setSnackbars(currentSnackbars => {
      const index = currentSnackbars.findIndex(snackbar => snackbar.key === key);
      if (index < 0 || currentSnackbars[index].exiting) {
        return currentSnackbars;
      }
      if (index >= MAX_VISIBLE_SNACKBARS) {
        return currentSnackbars.filter(snackbar => snackbar.key !== key);
      }
      return currentSnackbars.map(snackbar => snackbar.key === key ? {
        ...snackbar,
        exiting: true
      } : snackbar);
    });
  }, []);
  const removeSnackbar = React.useCallback((key: SnackbarKey) => {
    setSnackbars(currentSnackbars => currentSnackbars.filter(snackbar => snackbar.key !== key));
  }, []);
  const context = React.useMemo<ISnackbarContext>(() => ({
    enqueueSnackbar,
    closeSnackbar
  }), [enqueueSnackbar, closeSnackbar]);
  const visibleSnackbars = snackbars.slice(0, MAX_VISIBLE_SNACKBARS);
  const stack = <div className={classes.container} aria-label="Notifications">
            {visibleSnackbars.map(snackbar => <Snackbar key={snackbar.key} snackbar={snackbar} onClose={closeSnackbar} onExited={removeSnackbar} />)}
        </div>;
  return <SnackbarContext.Provider value={context} data-sentry-element="SnackbarContext.Provider" data-sentry-component="UnstyledSnackbarProvider" data-sentry-source-file="SnackbarProvider.tsx">
            {props.children}
            {typeof document !== 'undefined' ? ReactDom.createPortal(stack, document.body) : null}
        </SnackbarContext.Provider>;
};
const StyledSnackbarProvider = injectSheet(SnackbarProviderClasses)(UnstyledSnackbarProvider);
export const SnackbarProvider = StyledSnackbarProvider;